import { Route, Routes } from 'react-router';
import { ToastContainer } from 'react-toastify';
import { useEffect, type FC, lazy } from 'react';
import { AuthService } from './services/AuthService';
import { PageWrapper } from './components/PageWrapper';
import { LazyWrapper } from './components/LazyRoute';
import { TechnicalErrorToastListener } from './components/TechnicalErrorToastListener/TechnicalErrorToastListener';
import { MercureEventsListener } from './components/MercureEventsListener/MercureEventsListener';
import { TabNotificationListener } from './components/TabNotificationListener/TabNotificationListener';
import { OrderToastStack } from './components/OrderToastStack/OrderToastStack';
import { CallInfoModal } from './components/CallInfoModal/CallInfoModal';
import {
  NavigationProgressWatcher,
  RouteProgressBeacon
} from './components/NavigationProgress';
import { useAppDispatch } from './store/reduxHooks';
import { setUserChecked, setTokensReady } from './store/auth';
import { setAccountProfile } from './store/account';
import { registerAudioUnlock } from './services/sound/audioUnlock';
import { AppErrorBoundary } from './components/ErrorBoundary';
import { YmapsProvider } from './contexts/YmapsContext';
import { isUiKitPageAllowed } from './utils/isUiKitPageAllowed';

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap';

const MainPage = lazy(() =>
  import('./pages/main').then((module) => ({ default: module.MainPage }))
);
const VendorsPage = lazy(() =>
  import('./pages/vendor-actions').then((module) => ({
    default: module.VendorsPage
  }))
);

const ApplicationsPage = lazy(() =>
  import('./pages/vendor-applications').then((module) => ({
    default: module.ApplicationsPage
  }))
);

const OrderPage = lazy(() =>
  import('./pages/orderV2').then((module) => ({ default: module.OrderPageV2 }))
);
const ProductPage = lazy(() =>
  import('./pages/product').then((module) => ({ default: module.ProductPage }))
);
const ClientPage = lazy(() =>
  import('./pages/client').then((module) => ({ default: module.ClientPage }))
);
const LoginPage = lazy(() =>
  import('./pages/loginV2').then((module) => ({
    default: module.LoginPageV2
  }))
);
const OrdersPage = lazy(() =>
  import('./pages/orders').then((module) => ({ default: module.OrdersPage }))
);
const ProductsPage = lazy(() =>
  import('./pages/products').then((module) => ({
    default: module.ProductsPage
  }))
);
const PromocodesPage = lazy(() =>
  import('./pages/promocodes').then((module) => ({
    default: module.PromocodesPage
  }))
);
const PageClients = lazy(() =>
  import('./pages/clients/PageClients').then((module) => ({
    default: module.PageClients
  }))
);
const PaymentRefundsPage = lazy(() =>
  import('./pages/payment-refunds/PaymentRefundsPage').then((module) => ({
    default: module.PaymentRefundsPage
  }))
);
// const AddedProductsPage = lazy(() =>
//   import('./pages/added-products').then((module) => ({
//     default: module.AddedProductsPage
//   }))
// );
// const NotAddedProductsPage = lazy(() =>
//   import('./pages/not-added-products').then((module) => ({
//     default: module.NotAddedProductsPage
//   }))
// );
// const OrderedGoodsPage = lazy(() =>
//   import('./pages/ordered-goods').then((module) => ({
//     default: module.OrderedGoodsPage
//   }))
// );
const BannersPage = lazy(() =>
  import('./pages/banners').then((module) => ({ default: module.BannersPage }))
);
const CheaperOrdersPage = lazy(() =>
  import('./pages/cheapers').then((module) => ({
    default: module.CheaperOrdersPage
  }))
);
const CheaperOrderDetail = lazy(() =>
  import('./pages/cheaper').then((module) => ({ default: module.default }))
);
const BannersBrandsPage = lazy(() =>
  import('./pages/banners-brends').then((module) => ({
    default: module.BannersBrandsPage
  }))
);
const SettingPage = lazy(() =>
  import('./pages/settings').then((module) => ({ default: module.SettingPage }))
);
const ManagersPage = lazy(() =>
  import('./pages/managers/ManagersPage').then((module) => ({
    default: module.ManagersPage
  }))
);
const AccountPage = lazy(() =>
  import('./pages/account/AccountPage').then((module) => ({
    default: module.default
  }))
);
const ReviewsPage = lazy(() =>
  import('./pages/reviews').then((module) => ({
    default: module.ReviewsPage
  }))
);
const UiKitPage = lazy(() =>
  import('./pages/ui-kit').then((module) => ({
    default: module.UiKitPage
  }))
);
// TODO: добавить страницу с вопросами, когда будет готов бэкенд для нее
// const QuestionsPage = lazy(() =>
//   import('./pages/questions').then((module) => ({
//     default: module.QuestionsPage
//   }))
// );

const App: FC = () => {
  const dispatch = useAppDispatch();

  // Разблокировка аудио на первый жест пользователя (обход autoplay policy).
  // Висит с самого старта, включая страницу логина, чтобы поймать первый клик.
  useEffect(() => {
    registerAudioUnlock();
  }, []);

  useEffect(() => {
    const check = async () => {
      try {
        if (AuthService.isAuthorized) {
          // Токены уже в localStorage (F5/возврат в приложение) — сообщаем
          // реактивно, чтобы сокет стартовал без ожидания логина.
          dispatch(setTokensReady(true));

          // Сначала восстанавливаем данные профиля из localStorage
          const savedProfile = localStorage.getItem('accountProfile');
          if (savedProfile !== null) {
            try {
              dispatch(setAccountProfile(JSON.parse(savedProfile)));
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Ошибка парсинга профиля из localStorage:', error);
            }
          }

          // Затем проверяем токен (не перезаписывая account)
          await AuthService.checkUser();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Ошибка при проверке пользователя:', error);
      } finally {
        dispatch(setUserChecked(true));
      }
    };
    void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App">
      <NavigationProgressWatcher />
      <TechnicalErrorToastListener />
      <MercureEventsListener />
      <TabNotificationListener />
      <OrderToastStack />
      <CallInfoModal />
      <ToastContainer />
      <AppErrorBoundary scope="global">
        <YmapsProvider>
          <Routes>
            <Route path="/" element={<LazyWrapper component={MainPage} />} />
            <Route
              path="/order/:uuid"
              element={<LazyWrapper component={OrderPage} />}
            />
            <Route
              path="/product/:id"
              element={<LazyWrapper component={ProductPage} />}
            />
            <Route
              path="/client/:id"
              element={<LazyWrapper component={ClientPage} />}
            />
            <Route
              path="/login"
              element={
                <LazyWrapper component={LoginPage} withPageWrapper={false} />
              }
            />
            <Route
              path="/orders"
              element={<LazyWrapper component={OrdersPage} />}
            />
            <Route
              path="/products"
              element={<LazyWrapper component={ProductsPage} />}
            />
            <Route
              path="/promocodes"
              element={<LazyWrapper component={PromocodesPage} />}
            />
            <Route
              path="/clients"
              element={<LazyWrapper component={PageClients} />}
            />
            <Route
              path="/payment-refunds"
              element={<LazyWrapper component={PaymentRefundsPage} />}
            />
            <Route
              path="/vendor-actions"
              element={<LazyWrapper component={VendorsPage} />}
            />
            <Route
              path="/vendor-applications"
              element={<LazyWrapper component={ApplicationsPage} />}
            />
            {/* <Route
          path="/added-products"
          element={<LazyWrapper component={AddedProductsPage} />}
        />
        <Route
          path="/not-added-products"
          element={<LazyWrapper component={NotAddedProductsPage} />}
        />
        <Route
          path="/ordered-goods"
          element={<LazyWrapper component={OrderedGoodsPage} />}
        /> */}
            <Route
              path="/banners"
              element={<LazyWrapper component={BannersPage} />}
            />
            <Route
              path="/cheapers"
              element={<LazyWrapper component={CheaperOrdersPage} />}
            />
            <Route
              path="/cheaper/:id"
              element={<LazyWrapper component={CheaperOrderDetail} />}
            />
            <Route
              path="/banners-brends"
              element={<LazyWrapper component={BannersBrandsPage} />}
            />
            <Route
              path="/settings"
              element={<LazyWrapper component={SettingPage} />}
            />
            <Route
              path="/managers"
              element={<LazyWrapper component={ManagersPage} />}
            />
            <Route
              path="/account"
              element={<LazyWrapper component={AccountPage} />}
            />
            <Route
              path="/reviews"
              element={<LazyWrapper component={ReviewsPage} />}
            />
            {/* <Route
        // TODO: добавить страницу с вопросами, когда будет готов бэкенд для нее
          path="/questions"
          element={<LazyWrapper component={QuestionsPage} />}
        /> */}
            {/* Тестовая страница ErrorBoundary — только при включённом флаге
            VITE_UI_KIT_PAGE_ALLOW; иначе роута нет (заход по URL → 404). */}
            {isUiKitPageAllowed() && (
              <Route
                path="/ui-kit"
                element={<LazyWrapper component={UiKitPage} />}
              />
            )}
            <Route
              path="*"
              element={
                <PageWrapper>
                  <RouteProgressBeacon />
                  <div>Страница не найдена 404</div>
                </PageWrapper>
              }
            />
          </Routes>
        </YmapsProvider>
      </AppErrorBoundary>
    </div>
  );
};

export default App;
